import { useState } from "react";
import { NavLink } from "react-router-dom";
import { getUser } from "../utils/auth";
import { MENU } from "../config/menuConfig";
import { filterMenuByAccess } from "../utils/filterMenu";
import { Menu, ChevronLeft } from "lucide-react";

export default function Sidebar({ isOpen, toggleSidebar, onLogout }) {
  const filteredMenu = filterMenuByAccess(MENU);

  const user = getUser();
  const currentUserId = user?.id;

  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(
    window.innerWidth >= 768 ? true : false
  );

  const toggleSubmenu = (label) => {
    setOpenSubmenu(openSubmenu === label ? null : label);
  };

  const getDynamicPath = (path) => {
    if (!path) return "";
    return path.replace(":id", currentUserId);
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 md:hidden ${isOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
        onClick={toggleSidebar}
      ></div>

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full z-50 bg-gradient-to-b from-gray-900 via-gray-800 to-black border-r border-white/10 backdrop-blur-xl backdrop-blur-md text-white transform transition-all duration-300 ease-out shadow-2xl ${isOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 md:static ${isCollapsed ? "md:w-[90px]" : "md:w-[260px]"} w-64 flex flex-col`}
      >
        {/* HEADER */}
        <div className="flex flex-col items-center justify-center p-4 border-b border-gray-700 relative ">
          {/* Desktop Collapse Button */}
          <button
            className="hidden md:flex absolute top-2 right-8 p-1 rounded hover:bg-gray-700 transition-all duration-300 hover:scale-110 my-1"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? <Menu size={20} /> : <Menu size={20} />}
          </button>

          {/* Logo */}
          <img
            src="/logo_htcl.png"
            alt="ERP Logo"
            className="relative top-2 w-16 h-16 object-contain transition-all duration-500 hover:scale-110 drop-shadow-[0_0_15px_rgba(59,130,246,0.4)] my-2"
          />

          {/* Title */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${isCollapsed
              ? "opacity-0 max-w-0 max-h-0"
              : "opacity-100 max-w-xs max-h-20 mt-2"
              }
             text-center`}
          >
            <h2 className="font-semibold text-lg tracking-wide text-center whitespace-nowrap">
              ERP Panel
            </h2>
          </div>

          {/* Mobile Close */}
          <button
            className="sm:hidden mt-2 text-2xl font-bold"
            onClick={toggleSidebar}
          >
            &#x2715;
          </button>
        </div>

        {/* MENU */}
        <nav className="flex-1 overflow-y-auto py-2">
          <ul className="space-y-1 px-2">
            {filteredMenu.map((item) => {
              const Icon = item.icon;

              const isParentActive =
                item.submenu &&
                item.submenu.some((child) =>
                  window.location.pathname.startsWith(
                    getDynamicPath(child.path)
                  )
                );

              return (
                <li key={item.label}>
                  {item.submenu ? (
                    <div>
                      {/* Parent Menu */}
                      <div
                        title={isCollapsed ? item.label : ""}
                        className={`group flex items-center justify-between cursor-pointer px-3 py-1 rounded-xl transition-all duration-300 hover:bg-white/10 hover:translate-x-1 ${isParentActive ? "bg-blue-500/15 text-blue-400 border border-blue-500/20 shadow-lg" : ""}`}
                        onClick={() => {
                          if (isCollapsed) {
                            setIsCollapsed(false);
                            setTimeout(() => {
                              setOpenSubmenu(item.label);
                            }, 300);
                          } else {
                            toggleSubmenu(item.label);
                          }
                        }}
                      >
                        <div className="flex items-center gap-3 overflow-hidden">
                          {Icon && (
                            <Icon
                              size={20}
                              className="transition-all duration-300 group-hover:text-blue-400 group-hover:scale-110"
                            />
                          )}

                          <span
                            className={`
                              whitespace-wrap overflow-hidden
                              transition-all duration-300
                              ${isCollapsed
                                ? "max-w-0 opacity-0 whitespace-nowrap"
                                : "max-w-[200px] opacity-100"
                              }
                            `}
                          >
                            {item.label}
                          </span>
                        </div>

                        {!isCollapsed && (
                          <span className={`transition-transform duration-300 ease-in-out ${openSubmenu === item.label ? "rotate-180" : ""}`}>▼</span>
                        )}
                      </div>

                      {/* Submenu */}
                      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubmenu === item.label && !isCollapsed ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                        <ul className="ml-4 border-l border-gray-700 pl-2 mt-1 space-y-1">
                          {item.submenu.map((child) => {
                            const ChildIcon = child.icon;

                            return (
                              <li key={child.path}>
                                <NavLink
                                  to={getDynamicPath(child.path)}
                                  title={child.label}
                                  className={({ isActive }) =>
                                    `
                                      group flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 hover:bg-gray-700 hover:translate-x-1
                                      ${isActive
                                      ? "bg-gray-700 text-blue-400 shadow-sm"
                                      : ""
                                    }
                                    `
                                  }
                                >
                                  {ChildIcon && (
                                    <ChildIcon
                                      size={18}
                                      className="transition-transform duration-300 group-hover:scale-110"
                                    />
                                  )}

                                  <span>{child.label}</span>
                                </NavLink>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  ) : item.external ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={isCollapsed ? item.label : ""}
                      className="group flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 hover:bg-white/10 hover:translate-x-1"
                      onClick={() => {
                        if (window.innerWidth < 768) {
                          toggleSidebar();
                        }
                      }}
                    >
                      {Icon && (
                        <Icon
                          size={20}
                          className="transition-all duration-300 group-hover:text-blue-400 group-hover:scale-110"
                        />
                      )}

                      <span
                        className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed
                            ? "max-w-0 opacity-0"
                            : "max-w-[200px] opacity-100"
                          }`}
                      >
                        {item.label}
                      </span>
                    </a>
                  ) : (
                    <NavLink
                      to={getDynamicPath(item.path)}
                      title={isCollapsed ? item.label : ""}
                      className={({ isActive }) =>
                        `group flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 hover:bg-white/10 hover:translate-x-1 ${isActive
                          ? "bg-blue-500/15 text-blue-400 border border-blue-500/20 shadow-lg"
                          : ""
                        }`
                      }
                      onClick={() => {
                        if (window.innerWidth < 768) {
                          toggleSidebar();
                        }
                      }}
                    >
                      {Icon && (
                        <Icon
                          size={20}
                          className="transition-all duration-300 group-hover:text-blue-400 group-hover:scale-110"
                        />
                      )}

                      <span
                        className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed
                            ? "max-w-0 opacity-0"
                            : "max-w-[200px] opacity-100"
                          }`}
                      >
                        {item.label}
                      </span>
                    </NavLink>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* FOOTER */}
        <div className="border-t border-gray-700 p-4">
          <button
            type="button"
            onClick={onLogout}
            title="Logout"
            className="w-full bg-red-600 hover:bg-red-700 hover:scale-105 transition-all duration-300 p-3 rounded-xl shadow-lg flex items-center justify-center font-medium"
          >
            {isCollapsed ? "↩" : "Logout"}
          </button>
        </div>
      </div>
    </>
  );
}